import { apiService } from '../../../components/api/apiService';
import { logLlmGateway } from './llmGatewayLogger';
import { normalizeGovernance } from './llmGatewayConstants';
import type {
  GatewayFormState,
  GatewayStatus,
  GovernanceSettings,
  LlmGateway,
  RegisteredLlm,
} from './llmGatewayTypes';

const CONNECTION = 'backend_py';
const BASE = '/api/v1/llm-gateway';

function serviceErrorMessage(err: unknown): string {
  const axiosErr = err as {
    code?: string;
    response?: { data?: { error?: { message?: string }; message?: string }; status?: number };
    message?: string;
  };
  if (axiosErr.code === 'ERR_NETWORK' || axiosErr.message === 'Network Error') {
    return 'Cannot reach BackendPYApp at http://127.0.0.1:8014. Start it with `python run.py` from BackendPYApp.';
  }
  return (
    axiosErr.response?.data?.error?.message ||
    axiosErr.response?.data?.message ||
    axiosErr.message ||
    'LLM Gateway request failed'
  );
}

function asArray<T>(value: unknown): T[] {
  if (Array.isArray(value)) return value as T[];
  return [];
}

function normalizeGateway(raw: Record<string, unknown>): LlmGateway {
  return {
    id: String(raw.id || ''),
    name: String(raw.name || ''),
    llmType: raw.llmType as LlmGateway['llmType'],
    modelId: String(raw.modelId || ''),
    provider: raw.provider as LlmGateway['provider'],
    apiTypes: asArray<LlmGateway['apiTypes'][number]>(raw.apiTypes),
    customEndpoint: String(raw.customEndpoint || ''),
    authMethod: (raw.authMethod as LlmGateway['authMethod']) || 'api_key',
    governance: normalizeGovernance(raw.governance as GovernanceSettings),
    status: (raw.status as GatewayStatus) || 'draft',
    publicAlias: String(raw.publicAlias || ''),
    routeKey: String(raw.routeKey || ''),
    createdAt: String(raw.createdAt || ''),
    updatedAt: String(raw.updatedAt || ''),
    hasApiKey: Boolean(raw.hasApiKey),
    apiKeyLast4: typeof raw.apiKeyLast4 === 'string' ? raw.apiKeyLast4 : null,
  };
}

export const llmGatewayApi = {
  async listModels(): Promise<RegisteredLlm[]> {
    logLlmGateway('app', 'models_load');
    try {
      const data = (await apiService.get(`${BASE}/models`, {}, CONNECTION)) as { items?: RegisteredLlm[] };
      const items = asArray<RegisteredLlm>(data.items);
      logLlmGateway('app', 'models_load_ok', { count: items.length });
      return items;
    } catch (err) {
      const message = serviceErrorMessage(err);
      logLlmGateway('error', 'models_load_failed', { message });
      throw new Error(message);
    }
  },

  async listGateways(): Promise<LlmGateway[]> {
    logLlmGateway('app', 'gateways_load');
    try {
      const data = (await apiService.get(`${BASE}/gateways`, {}, CONNECTION)) as { items?: Record<string, unknown>[] };
      const items = asArray<Record<string, unknown>>(data.items).map(normalizeGateway);
      logLlmGateway('app', 'gateways_load_ok', { count: items.length });
      return items;
    } catch (err) {
      const message = serviceErrorMessage(err);
      logLlmGateway('error', 'gateways_load_failed', { message });
      throw new Error(message);
    }
  },

  async saveGateway(form: GatewayFormState, options: { id?: string; status: GatewayStatus }): Promise<LlmGateway> {
    const payload = {
      name: form.name.trim(),
      llmType: form.llmType,
      modelId: form.modelId,
      apiTypes: form.apiTypes,
      customEndpoint: form.customEndpoint.trim(),
      authMethod: form.authMethod,
      governance: form.governance,
      status: options.status,
    };
    try {
      const raw = options.id
        ? ((await apiService.put(`${BASE}/gateways/${options.id}`, payload, CONNECTION)) as Record<string, unknown>)
        : ((await apiService.post(`${BASE}/gateways`, payload, CONNECTION)) as Record<string, unknown>);
      logLlmGateway('app', options.id ? 'gateway_updated' : 'gateway_created', { id: raw.id, status: options.status });
      return normalizeGateway(raw);
    } catch (err) {
      const message = serviceErrorMessage(err);
      logLlmGateway('error', 'gateway_save_failed', { message, id: options.id });
      throw new Error(message);
    }
  },

  async updateStatus(id: string, status: GatewayStatus): Promise<LlmGateway> {
    try {
      const raw = (await apiService.patch(`${BASE}/gateways/${id}/status`, { status }, CONNECTION)) as Record<string, unknown>;
      logLlmGateway('app', 'gateway_status', { id, status });
      return normalizeGateway(raw);
    } catch (err) {
      const message = serviceErrorMessage(err);
      logLlmGateway('error', 'gateway_status_failed', { message, id });
      throw new Error(message);
    }
  },

  async updateGovernance(id: string, governance: GovernanceSettings): Promise<LlmGateway> {
    try {
      const raw = (await apiService.patch(`${BASE}/gateways/${id}/governance`, governance, CONNECTION)) as Record<string, unknown>;
      logLlmGateway('app', 'governance_updated', { id });
      return normalizeGateway(raw);
    } catch (err) {
      const message = serviceErrorMessage(err);
      logLlmGateway('error', 'governance_update_failed', { message, id });
      throw new Error(message);
    }
  },

  async deleteGateway(id: string): Promise<void> {
    try {
      await apiService.delete(`${BASE}/gateways/${id}`, CONNECTION);
      logLlmGateway('warning', 'gateway_deleted', { id });
    } catch (err) {
      const message = serviceErrorMessage(err);
      logLlmGateway('error', 'gateway_delete_failed', { message, id });
      throw new Error(message);
    }
  },

  async testConnection(id: string): Promise<{ ok: boolean; message: string }> {
    try {
      const data = (await apiService.post(`${BASE}/gateways/${id}/test-connection`, {}, CONNECTION)) as {
        ok?: boolean;
        message?: string;
      };
      logLlmGateway(data.ok ? 'app' : 'warning', 'test_connection', { id, ok: data.ok });
      return { ok: Boolean(data.ok), message: data.message || (data.ok ? 'Connection succeeded.' : 'Connection failed.') };
    } catch (err) {
      const message = serviceErrorMessage(err);
      logLlmGateway('error', 'test_connection_failed', { message, id });
      throw new Error(message);
    }
  },
};
